import _ from 'underscore';
import { Component } from 'react';
import styled from 'styled-components';
import { Icon } from '@workramp/ui';
import { SEARCH, SEARCH_RESULTS_CLICKED } from '~/constants/segmentActions';
import { segmentTracking } from '~/common/segment/track';
import withSearchResultsManagement from '~/components/common/search/with-search-results-management';
import withAlgoliaSearch from '~/components/common/search/with-algolia-search';
import AcademyIndices from '../../../algolia/academy/academy-indices';
import DropdownEnabledSearchBar from '../../common/search/dropdown-enabled-search-bar';
import AcademySearchBarResult from '../../../algolia/academy/academy-search-bar-result';

// Academy Learner Search
class AcademySearchBarBase extends Component {
  TRANSLATED_SEARCH = I18n.t('js.academy.navigation.search');

  state = {
    query: '',
    selected: false,
  };

  render() {
    return (
      <AcademySearchBarWrapper
        onClick={this.selectSelf}
        className="academy-search-bar-wrapper training-search-new training-search-new-2019"
        selected={this.state.selected}
      >
        <Icon name="search:regular" />
        <DropdownEnabledSearchBar
          controlledQuery={this.state.query}
          handleItemSelect={this.props.handleItemSelect}
          onBlur={this.onBlur}
          onReset={this.props.onReset}
          placeholder={this.TRANSLATED_SEARCH}
          renderResult={(result, isActive, index) => (
            <AcademySearchBarResult
              key={`${result?.name || 'result'}-${index}`}
              {...result}
              isActive={isActive}
              contentLabelOverrides={this.props.contentLabelOverrides}
              isExternal
              accessibilityProps={{
                role: 'option',
                tabIndex: 0,
              }}
            />
          )}
          results={this.props.results}
          search={this.search}
          searching={this.props.searching}
          updateControlledQuery={this.updateControlledQuery}
          debouncedSearchTime={APP_CONSTANTS.DEBOUNCED_SEARCH_TIME.SLOW}
        />
      </AcademySearchBarWrapper>
    );
  }

  selectSelf = () => {
    const { query } = this.state;
    this.setState({ selected: true });
    if (query.length) {
      this.segmentTrackSearch(SEARCH_RESULTS_CLICKED, query);
    }
  };

  onBlur = () => {
    this.setState({ selected: false });
    if (typeof this.props.onBlur === 'function') this.props.onBlur();
  };

  search = (query) => {
    if (query) {
      let filters = [];
      if (this.props.validTypes) {
        filters.push(
          `(${_.map(this.props.validTypes, (typeFilterValue) => `type:"${_.escape(typeFilterValue)}"`).join(' OR ')})`,
        );
      }

      // analyticsTags are used to filter data in search analytics
      const analyticsTags = ['external'];
      if (this.props.academyId) {
        filters.push(`(academy_id:"${this.props.academyId}")`);

        analyticsTags.push(`academy_id:${this.props.academyId}`);
      }
      this.segmentTrackSearch(SEARCH, query);
      const searchId = this.props.beforeSearch();

      filters = filters.join(' AND ');
      // TODO: see if we can just get rid of all the duplicative filters here...
      this.props.search(query, this.afterSearch.bind(this, searchId), {
        offset: 0,
        length: 250,
        filters,
        analyticsTags,
      });
    } else {
      this.props.clear();
    }
  };

  afterSearch = (searchId, results) => {
    this.props.filter(results, (filteredResults) => {
      let formattedResults = this.props.format(filteredResults);
      formattedResults = _.flatten(formattedResults);

      this.props.afterSearch(searchId, formattedResults);
    });
  };

  updateControlledQuery = (query) => {
    this.setState({ query });
  };

  static segmentTrackSearch = (segmentEventName, searchTerm) => {
    const segmentPayload = {
      segmentEventName,
      properties: {
        searchTerm,
      },
    };
    segmentTracking(segmentPayload);
  };
}

const AcademySearchBarWrapper = styled.div`
  box-sizing: border-box;
  flex: 0 0 200px;
  height: 32px;
  position: relative;
  & > svg {
    pointer-events: none;
    position: absolute;
    z-index: 16;
    left: 12px;
    top: 7px;
    color: #74777c;
    ${(props) => props.selected && `color: #444649;`}
  }
  .dropdown-enabled-search-bar {
    box-sizing: border-box;
    box-shadow: none;
    background: none;
    background-color: rgba(255, 255, 255);
    border: 1px solid rgba(27, 28, 28, 0.1);
    height: 32px;
    border-radius: 8px;
    ${(props) =>
      props.selected &&
      `
      border: solid 1px rgb(7, 75, 226);
      outline: rgba(7, 75, 226, 0.2) solid 4px;
      outline-offset: 0px;
      `}
    &:hover {
      border: 1px solid rgba(27, 28, 28, 0.25);
      ${(props) => props.selected && `border-color: rgb(7, 75, 226)`}
    }
    &.searching {
      border-radius: 8px;
    }
    .search-bar {
      input {
        box-sizing: border-box;
        width: 100%;
        height: 28px;
        padding: 0 24px 0 32px;
        background: none;
        font-size: 13px;
        color: #1b324b;
        font-weight: normal;
        letter-spacing: 0;
        &::placeholder {
          color: #74777c;
          letter-spacing: 0;
        }
      }
    }
    .search-bar-dropdown {
      border-radius: 6px !important;
      box-shadow: 0 4px 24px 2px rgba(20, 21, 22, 0.16);
      background-color: #fff;
      width: 500px;
      .search-loading-indicator {
        img {
          margin-right: auto;
        }
      }
      .default-search-result--inner {
        padding: 15px 20px;
        border: none;
        .default-search-result--details {
          width: auto;
          padding-right: 10px;
        }
        .default-search-result--type {
          display: block;
          flex: 0 0 100px;
          width: 100px;
          box-sizing: border-box;
          position: relative;
          right: auto;
          bottom: auto;
          width: auto;
          padding: 0 0 0 10px;
          span {
            font-size: 10px;
            color: ${({ theme }) => theme.colors.decorative.neutral[700]};
            position: relative;
            top: auto;
            right: auto;
            transform: none;
            background-color: rgba(27, 50, 75, 0.1);
            border-radius: 10px;
            padding: 5px 10px;
            text-transform: uppercase;
          }
        }
        .default-search-result--icon {
          display: none;
        }
        .default-search-result--label {
          padding-left: 0;
          .default-search-result--title {
            font-size: 13px;
            font-weight: normal;
            color: #1b324b;
          }
          .default-search-result--subtitle {
            font-size: 12px;
            color: #1b324b;
            opacity: 0.8;
          }
          .default-search-result--content {
            font-size: 12px;
            color: ${({ theme }) => theme.colors.decorative.neutral[700]};
          }
        }
      }
    }
  }
`;

const AcademySearchBar = withSearchResultsManagement(withAlgoliaSearch(AcademySearchBarBase, AcademyIndices.getAll()));
export default AcademySearchBar;
